/**
 * Everything the just-in-time PO page reads and writes (BLU-1268, plan 90
 * section 5.4), in one hook so the view stays presentational and its test can
 * mock one module.
 *
 * Reads: the line outcomes (ONE `get_jit_po_line_outcomes` RPC), the PO's files
 * (the receipt), and whether Xero is connected (asked only once the order is
 * completed, the one state that offers Push to Xero). The order itself comes from
 * the page (`getOrder`, already cached).
 *
 * Writes reuse the existing PO paths: `updateDraft` (lines, prices, supplier;
 * one call), `completeJustInTime` (cost only, through the shared
 * `useCompleteJustInTime`, also behind To buy), `updateOrderStatus` to
 * cancelled, `undoOrderStatus` (back to To buy), the PO file mutations and the
 * Xero bill push. Every write resolves to true or false; on failure it has
 * already raised a destructive toast in plain words (`jitPoErrorText`), so the
 * view only decides what stays open.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { reportError } from '@/lib/logging';
import type { PurchaseOrder } from '@/features/purchase-orders';
import { jitPoErrorText } from '../lib/jit-po-error-text';
import type { JitSavePayload } from '../lib/jit-lines';
import { readFileAsBase64 } from '@/lib/read-file-base64';
import { prepareReceiptFile, RECEIPT_MAX_BYTES } from '../lib/receipt-photo';
import { useCompleteJustInTime } from './useCompleteJustInTime';

export type PurchaseOrderFile = RouterOutputs['purchaseOrders']['listPurchaseOrderFiles'][number];

const OUTCOMES_STALE_MS = 30_000;
const FILES_STALE_MS = 30_000;

export function useJustInTimeOrder(order: PurchaseOrder) {
  const { t } = useTranslation('orders');
  const utils = trpc.useUtils();
  const orderId = order.id;
  const locationId = order.location_id ?? '';
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const outcomesQuery = trpc.purchaseOrders.getJitOutcomes.useQuery(
    { orderId },
    { staleTime: OUTCOMES_STALE_MS, retry: 1 },
  );
  const filesQuery = trpc.purchaseOrders.listPurchaseOrderFiles.useQuery({ orderId }, { staleTime: FILES_STALE_MS });
  // Only a completed order offers Push to Xero, so nothing else asks.
  const xeroStatusQuery = trpc.xero.getStatus.useQuery(undefined, {
    enabled: order.status === 'received',
    staleTime: 60_000,
    retry: false,
  });

  // Returned from onSuccess so mutateAsync resolves onto fresh data (DESIGN.md
  // section 12.2): the page never flashes the old lines after a save.
  const refreshOrder = () =>
    Promise.all([
      utils.purchaseOrders.getOrder.invalidate({ orderId }),
      utils.purchaseOrders.getJitOutcomes.invalidate({ orderId }),
      utils.purchaseOrders.getOrders.invalidate(),
    ]);
  const refreshFiles = () => utils.purchaseOrders.listPurchaseOrderFiles.invalidate({ orderId });

  const saveMutation = trpc.purchaseOrders.updateDraft.useMutation({ onSuccess: refreshOrder });
  const completeJustInTime = useCompleteJustInTime();
  const cancelMutation = trpc.purchaseOrders.updateOrderStatus.useMutation({ onSuccess: refreshOrder });
  const undoMutation = trpc.purchaseOrders.undoOrderStatus.useMutation({ onSuccess: refreshOrder });
  const uploadMutation = trpc.purchaseOrders.uploadPurchaseOrderFile.useMutation({ onSuccess: refreshFiles });
  const deleteFileMutation = trpc.purchaseOrders.deletePurchaseOrderFile.useMutation({ onSuccess: refreshFiles });
  const downloadUrlMutation = trpc.purchaseOrders.getPurchaseOrderFileDownloadUrl.useMutation();
  const pushXeroMutation = trpc.xero.pushPurchaseOrder.useMutation({ onSuccess: refreshOrder });

  const fail = (title: string, error: unknown, skuLabel?: (skuId: string) => string | null) => {
    toast({ title, description: jitPoErrorText(t, error, skuLabel), variant: 'destructive' });
  };

  const save = async (payload: JitSavePayload, skuLabel?: (skuId: string) => string | null): Promise<boolean> => {
    try {
      await saveMutation.mutateAsync(payload);
      return true;
    } catch (error) {
      fail(t('jit.toast.saveFailed'), error, skuLabel);
      return false;
    }
  };

  const complete = (asListed: boolean): Promise<boolean> => completeJustInTime.complete(order, asListed);

  const cancel = async (): Promise<boolean> => {
    try {
      await cancelMutation.mutateAsync({ orderId, locationId, status: 'cancelled' });
      toast({ title: t('jit.toast.cancelled'), description: order.po_number });
      return true;
    } catch (error) {
      fail(t('jit.toast.cancelFailed'), error);
      return false;
    }
  };

  const undoComplete = async (): Promise<boolean> => {
    try {
      await undoMutation.mutateAsync({ orderId, locationId });
      toast({ title: t('jit.toast.undone'), description: order.po_number });
      return true;
    } catch (error) {
      fail(t('jit.toast.undoFailed'), error);
      return false;
    }
  };

  const uploadReceipt = async (picked: File): Promise<boolean> => {
    setUploading(true);
    try {
      const file = await prepareReceiptFile(picked);
      if (file.size > RECEIPT_MAX_BYTES) {
        toast({ title: t('jit.toast.uploadFailed'), description: t('jit.toast.fileTooLarge'), variant: 'destructive' });
        return false;
      }
      const base64 = await readFileAsBase64(file);
      await uploadMutation.mutateAsync({
        orderId,
        fileType: 'invoice',
        fileName: file.name,
        contentType: file.type || null,
        base64,
      });
      return true;
    } catch (error) {
      reportError('Just-in-time receipt upload failed', error);
      fail(t('jit.toast.uploadFailed'), error);
      return false;
    } finally {
      setUploading(false);
    }
  };

  const deleteReceipt = async (fileId: string): Promise<boolean> => {
    setDeletingFileId(fileId);
    try {
      await deleteFileMutation.mutateAsync({ fileId });
      return true;
    } catch (error) {
      fail(t('jit.toast.deleteFailed'), error);
      return false;
    } finally {
      setDeletingFileId(null);
    }
  };

  const openReceipt = async (fileId: string): Promise<void> => {
    // Open the tab synchronously (inside the click) so a phone browser does not
    // block it as a pop-up, then point it at the signed URL once it arrives.
    const tab = typeof window !== 'undefined' ? window.open('', '_blank') : null;
    try {
      const { url } = await downloadUrlMutation.mutateAsync({ fileId });
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      tab?.close();
      fail(t('jit.toast.openFailed'), error);
    }
  };

  const pushToXero = async (): Promise<boolean> => {
    try {
      await pushXeroMutation.mutateAsync({ orderId });
      toast({ title: t('jit.toast.pushed'), description: order.po_number });
      return true;
    } catch (error) {
      fail(t('jit.toast.pushFailed'), error);
      return false;
    }
  };

  const receipts = (filesQuery.data ?? []).filter((file) => file.file_type === 'invoice');

  return {
    outcomes: {
      data: outcomesQuery.data ?? null,
      loading: outcomesQuery.isLoading,
      failed: Boolean(outcomesQuery.error) && !outcomesQuery.data,
      retrying: outcomesQuery.isFetching,
      retry: () => void outcomesQuery.refetch(),
    },
    receipts: {
      files: receipts,
      loading: filesQuery.isLoading,
      failed: Boolean(filesQuery.error) && !filesQuery.data,
      retrying: filesQuery.isFetching,
      retry: () => void filesQuery.refetch(),
    },
    xeroConnected: xeroStatusQuery.data?.connected === true,
    save,
    complete,
    cancel,
    undoComplete,
    uploadReceipt,
    deleteReceipt,
    openReceipt,
    pushToXero,
    pending: {
      saving: saveMutation.isPending,
      completing: completeJustInTime.pending,
      cancelling: cancelMutation.isPending,
      undoing: undoMutation.isPending,
      uploading,
      deletingFileId,
      pushing: pushXeroMutation.isPending,
    },
  };
}

export type JustInTimeOrderState = ReturnType<typeof useJustInTimeOrder>;
